import {DateTime} from 'luxon';
import { assoc, dissoc, indexBy, mergeLeft, prepend, uniq, without } from 'ramda';
import {AnyAction, combineReducers} from 'redux';

import { parseCheckpointType } from '../checkpointTypes/reducer';

import {
  isClearRecentlyModified,
  isCreateCheckpointSuccess,
  isDeleteCheckpointSuccess,
  isGetCheckpointCountSuccess,
  isGetCheckpointsSuccess,
  isGetSingleCheckpointSuccess,
  isUpdateCheckpointSuccess
} from './actions';
import {
  Checkpoint,
  CheckpointResponse,
  Checkpoints
} from './state';

export const INITIAL_STATE: Checkpoints = {
  allCodes: null,
  byCode: {},
  count: null,
  recentlyModified: []
};

export const checkpoints = combineReducers({
  allCodes,
  byCode,
  count,
  recentlyModified
});

const indexByCode = indexBy<Checkpoint>((x: Checkpoint) => x.code);
export const parseCheckpoint = (response: CheckpointResponse): Checkpoint => ({
  ...response,
  created: DateTime.fromISO(response.created),
  types: response.types.map(parseCheckpointType),
  updated: DateTime.fromISO(response.updated)
});

function byCode(state = INITIAL_STATE.byCode, action: AnyAction): typeof INITIAL_STATE.byCode {
  if (isGetCheckpointsSuccess(action)) {
    const parsed = action.payload.data.map(parseCheckpoint);
    return mergeLeft(indexByCode(parsed), state);
  } else if (isDeleteCheckpointSuccess(action)) {
    return dissoc(action.meta.previousAction.code, state);
  }

  if (isCreateCheckpointSuccess(action)
    || isUpdateCheckpointSuccess(action)
    || isGetSingleCheckpointSuccess(action)
  ) {
    return assoc(action.payload.data.code, parseCheckpoint(action.payload.data), state);
  }

  return state;
}

function allCodes(state = INITIAL_STATE.allCodes, action: AnyAction): typeof INITIAL_STATE.allCodes {
  if (isGetCheckpointsSuccess(action)) {
    return action.payload.data.map((c) => c.code);
  } else if (isDeleteCheckpointSuccess(action)) {
    return without([action.meta.previousAction.code], state || []);
  }

  if (isCreateCheckpointSuccess(action)) {
    return prepend(action.payload.data.code, state || []);
  }

  if (isGetSingleCheckpointSuccess(action)) {
    return uniq(prepend(action.payload.data.code, state || []));
  }

  return state;
}

function count(state = INITIAL_STATE.count, action: AnyAction): typeof INITIAL_STATE.count {
  if (isGetCheckpointCountSuccess(action)) {
    return action.payload.data;
  } else if (isDeleteCheckpointSuccess(action) && state !== null) {
    return state - 1;
  }

  if (isCreateCheckpointSuccess(action)) {
    return state ? (state + 1) : 1;
  }
  return state;
}

function recentlyModified(
  state = INITIAL_STATE.recentlyModified,
  action: AnyAction
): typeof INITIAL_STATE.recentlyModified {
  if (isClearRecentlyModified(action)) {
    return [];
  } else if (isDeleteCheckpointSuccess(action)) {
    return without([action.meta.previousAction.code], state);
  }

  if (isCreateCheckpointSuccess(action) || isUpdateCheckpointSuccess(action)) {
    return prepend(action.payload.data.code, state);
  }

  return state;
}
